import { apiFetch } from '@/stores/authStore';
import type { InstitutionalDiscoveryRequest } from './institutionalDiscoveryClient';

export type InstitutionalReferenceRequest = Omit<InstitutionalDiscoveryRequest, 'participantId'> & { symbol: string };
const eventFields = ['question', 'payoutValue', 'evaluationType', 'eventId', 'eventDisplayName', 'strikeValue',
  'strikeUnit', 'calculationMethod', 'positionAccountabilityValue', 'eventOutcome'] as const;
export type InstitutionalNativeTerms = {
  baseCurrency: string | null; multiplier: number | null; productId: string | null;
  settlementCurrency: string | null; settlementPriceScale: string | null;
  priceLimit: Partial<{ low: string; high: string; lowSet: boolean; highSet: boolean;
    relativeLow: number; relativeHigh: number; relativeLowSet: boolean; relativeHighSet: boolean }> | null;
  orderSizeLimit: Partial<{ low: string; high: string; lowSet: boolean; highSet: boolean;
    totalNotionalLow: string; totalNotionalHigh: string; totalNotionalLowSet: boolean; totalNotionalHighSet: boolean }> | null;
  eventAttributes: (Partial<Record<typeof eventFields[number], string>> & {
    timeSpecifier?: { year?: number; month?: number; day?: number };
  }) | null;
};
export type InstitutionalReference = {
  kind: 'institutional_reference_check'; mappingState: 'unproved'; environment: 'preprod' | 'prod'; symbol: string;
  observedAtMs: number; expiresAtMs: number;
  instrument: {
    symbol: string; state: string; nonTradable: boolean | null;
    priceScale: string; fractionalQtyScale: string; tickSize: string | null; minimumTradeQty: string | null;
    metadata: Record<string, string>; updateTime: string | null; revision: string; nativeTerms: InstitutionalNativeTerms;
  };
};
export type CheckInstitutionalReference = (request: InstitutionalReferenceRequest, signal: AbortSignal) => Promise<InstitutionalReference>;
const errors = {
  invalid: 'The instrument check returned an invalid response. Try again.',
  incomplete: 'The instrument check did not complete. Try again.',
  session: 'Your session has expired. Sign in again.',
  access: 'You do not have access to check this instrument.',
  busy: 'Connection checks are busy. Try again shortly.',
  configuration: 'Instrument checking is not configured on this site.',
  provider: 'Could not complete the provider instrument check. Try again later.',
  key: 'Choose a valid RSA private key in PEM format.',
  refused: 'Could not check this instrument. Review the connection and exact symbol.',
} as const;
export class InstitutionalReferenceError extends Error {
  constructor(reason: keyof typeof errors) { super(errors[reason]); }
}
const fail = (): never => { throw new InstitutionalReferenceError('invalid'); };
const record = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : fail();
const text = (value: unknown, max = 2048, empty = false): string => typeof value === 'string'
  && (empty || value.length > 0) && value.length <= max && !/[\x00-\x1f\x7f]/.test(value) ? value : fail();
const nullableText = (value: unknown, max = 2048, empty = false): string | null => value === null ? null : text(value, max, empty);
const divisor = (value: unknown): string => typeof value === 'string' && /^10{0,18}$/.test(value) ? value : fail();
const time = (value: unknown): number => Number.isSafeInteger(value) && Number(value) >= 0
  && Number.isFinite(new Date(Number(value)).getTime()) ? Number(value) : fail();
const integer = (value: unknown): string => {
  const result = text(value, 20);
  if (!/^(?:0|-[1-9]\d*|[1-9]\d*)$/.test(result)
    || BigInt(result) < -9223372036854775808n || BigInt(result) > 9223372036854775807n) return fail();
  return result;
};
function limit(value: unknown, type: 'price'): InstitutionalNativeTerms['priceLimit'];
function limit(value: unknown, type: 'size'): InstitutionalNativeTerms['orderSizeLimit'];
function limit(value: unknown, type: 'price' | 'size') {
  if (value === null) return null;
  const raw = record(value), result: Record<string, string | number | boolean> = {};
  const ints = ['low', 'high', ...(type === 'size' ? ['totalNotionalLow', 'totalNotionalHigh'] : [])];
  const flags = ['lowSet', 'highSet', ...(type === 'size' ? ['totalNotionalLowSet', 'totalNotionalHighSet'] : ['relativeLowSet', 'relativeHighSet'])];
  for (const key of ints) if (raw[key] != null) result[key] = integer(raw[key]);
  for (const key of flags) if (raw[key] != null) {
    if (typeof raw[key] !== 'boolean') return fail();
    result[key] = raw[key];
  }
  if (type === 'price') for (const key of ['relativeLow', 'relativeHigh']) if (raw[key] != null) {
    if (typeof raw[key] !== 'number' || !Number.isFinite(raw[key])) return fail();
    result[key] = raw[key];
  }
  return result;
}
const states = ['OPEN', 'PREOPEN', 'SUSPENDED', 'EXPIRED', 'TERMINATED', 'HALTED', 'MATCH_AND_CLOSE_AUCTION', 'PENDING']
  .map(state => `INSTRUMENT_STATE_${state}`);

/** Display evidence only. Exact symbol equality is not a public-market binding,
 * and raw payout/multiplier terms must not become inferred execution units. */
export function decodeInstitutionalReference(raw: unknown, expected: Pick<InstitutionalReferenceRequest, 'environment' | 'symbol'>): InstitutionalReference {
  const r = record(record(raw).reference), i = record(r.instrument), n = record(i.nativeTerms), metadata = record(i.metadata);
  if (r.kind !== 'institutional_reference_check' || r.mappingState !== 'unproved'
    || r.environment !== expected.environment || r.symbol !== expected.symbol || i.symbol !== r.symbol
    || !states.includes(String(i.state)) || !(i.nonTradable === null || typeof i.nonTradable === 'boolean')
    || typeof i.revision !== 'string' || !/^[a-f0-9]{64}$/.test(i.revision)) return fail();
  const observedAtMs = time(r.observedAtMs), expiresAtMs = time(r.expiresAtMs);
  if (expiresAtMs <= observedAtMs || expiresAtMs - observedAtMs > 60_000) return fail();
  const entries = Object.entries(metadata);
  if (entries.length > 128) return fail();
  const cleanMetadata = Object.fromEntries(entries.map(([key, value]) => [text(key, 128), text(value, 2048, true)]));
  let eventAttributes: InstitutionalNativeTerms['eventAttributes'] = null;
  if (n.eventAttributes !== null) {
    const attrs = record(n.eventAttributes);
    eventAttributes = {};
    for (const field of eventFields) if (attrs[field] !== undefined) eventAttributes[field] = text(attrs[field], 2048, true);
    if (attrs.timeSpecifier !== undefined) {
      const stamp = record(attrs.timeSpecifier), date: NonNullable<InstitutionalNativeTerms['eventAttributes']>['timeSpecifier'] = {};
      for (const [key, min, max] of [['year', 0, 9999], ['month', 0, 12], ['day', 0, 31]] as const) {
        if (stamp[key] !== undefined) {
          if (!Number.isSafeInteger(stamp[key]) || Number(stamp[key]) < min || Number(stamp[key]) > max) return fail();
          date[key] = Number(stamp[key]);
        }
      }
      eventAttributes.timeSpecifier = date;
    }
  }
  if (n.multiplier !== null && (typeof n.multiplier !== 'number' || !Number.isFinite(n.multiplier))) return fail();
  const tickSize = nullableText(i.tickSize, 128), minimumTradeQty = nullableText(i.minimumTradeQty, 20);
  if (tickSize !== null && (!/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(tickSize) || Number(tickSize) <= 0)
    || minimumTradeQty !== null && (!/^(?:0|[1-9]\d*)$/.test(minimumTradeQty) || BigInt(minimumTradeQty) > 9223372036854775807n)) return fail();
  const updateTime = nullableText(i.updateTime, 64);
  if (updateTime !== null && !Number.isFinite(Date.parse(updateTime))) return fail();
  const settlementPriceScale = n.settlementPriceScale === null ? null : integer(n.settlementPriceScale);
  return { kind: 'institutional_reference_check', mappingState: 'unproved', environment: expected.environment,
    symbol: text(r.symbol, 256), observedAtMs, expiresAtMs,
    instrument: { symbol: text(i.symbol, 256), state: String(i.state), nonTradable: i.nonTradable,
      priceScale: divisor(i.priceScale), fractionalQtyScale: divisor(i.fractionalQtyScale), tickSize, minimumTradeQty,
      metadata: cleanMetadata, updateTime, revision: i.revision,
      nativeTerms: { baseCurrency: nullableText(n.baseCurrency, 2048, true), multiplier: n.multiplier,
        productId: nullableText(n.productId, 2048, true), settlementCurrency: nullableText(n.settlementCurrency, 2048, true),
        settlementPriceScale, priceLimit: limit(n.priceLimit, 'price'), orderSizeLimit: limit(n.orderSizeLimit, 'size'), eventAttributes } } };
}

export async function checkInstitutionalReference(request: InstitutionalReferenceRequest,
  signal: AbortSignal, orgQueryParam = ''): Promise<InstitutionalReference> {
  let response: Response;
  try {
    response = await apiFetch(`/api/manager/polymarket-us/institutional-connections/reference-check${orgQueryParam}`, {
      method: 'POST', signal, cache: 'no-store', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request),
    });
  } catch { throw new InstitutionalReferenceError('incomplete'); }
  if (!response.ok) {
    const raw: unknown = await response.json().catch(() => null);
    const code = raw && typeof raw === 'object' && 'code' in raw ? raw.code : null;
    const reason = response.status === 401 ? 'session' : response.status === 403 ? 'access'
      : code === 'US_CREDENTIAL_CHECK_BUSY' || response.status === 429 ? 'busy'
        : code === 'US_INSTITUTIONAL_REFERENCE_COORDINATION_REQUIRED' ? 'configuration'
          : code === 'US_INSTITUTIONAL_REFERENCE_UNAVAILABLE' ? 'provider'
            : code === 'US_INSTITUTIONAL_KEY_INVALID' ? 'key' : 'refused';
    throw new InstitutionalReferenceError(reason);
  }
  return decodeInstitutionalReference(await response.json().catch(() => null), request);
}
